import React, { useState, memo } from 'react';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';

/**
 * Export Audit Logs Button Component
 * 
 * Allows librarians to export audit logs to CSV format.
 * Only visible to librarians.
 * 
 * Features:
 * - Optional date range filtering
 * - Downloads CSV via pre-signed S3 URL
 * - Shows record count and expiry time
 */
const ExportAuditButton = memo(({ isLibrarian, token, settings }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Don't render if user is not a librarian
  if (!isLibrarian) {
    return null;
  }

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      // Build API endpoint URL
      const apiEndpoint = settings?.apiEndpoint || '';
      const baseUrl = apiEndpoint.replace('/data', '');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const queryString = params.toString();
      const url = `${baseUrl}/admin/audit-export${queryString ? `?${queryString}` : ''}`;

      // Make API request
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token.jwtToken || token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Download CSV via pre-signed URL
      if (data.downloadUrl) {
        // Create temporary link and trigger download
        const link = document.createElement('a');
        link.href = data.downloadUrl;
        link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        alert(`Successfully exported ${data.recordCount} audit log records.\n\nThe download should start automatically.`);
        
        // Close modal
        setIsModalOpen(false);
        setStartDate('');
        setEndDate('');
      } else {
        throw new Error('No download URL received from server');
      }
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      setError(err.message || 'Failed to export audit logs');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        iconName="download"
        variant="normal"
      >
        Export Audit Logs
      </Button>

      <Modal
        visible={isModalOpen}
        onDismiss={() => {
          if (!isExporting) {
            setIsModalOpen(false);
            setError(null);
            setStartDate('');
            setEndDate('');
          }
        }}
        header="Export Audit Logs"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setIsModalOpen(false);
                  setError(null);
                  setStartDate('');
                  setEndDate('');
                }}
                disabled={isExporting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleExport}
                disabled={isExporting}
                iconName="download"
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {isExporting && (
            <Box textAlign="center">
              <SpaceBetween size="s">
                <Spinner size="large" />
                <Box variant="p">
                  Generating CSV file... This may take a moment for large log sets.
                </Box>
              </SpaceBetween>
            </Box>
          )}

          {!isExporting && (
            <>
              <Box variant="p">
                Export all audit logs to a CSV file. You can optionally filter by date range.
              </Box>

              <Alert type="info">
                The CSV file will include all audit events: queries, imports, updates, deletes, and API key retrievals.
              </Alert>

              <FormField
                label="Start Date (optional)"
                description="Filter logs from this date onwards (YYYY-MM-DD)"
              >
                <DatePicker
                  value={startDate}
                  onChange={({ detail }) => setStartDate(detail.value)}
                  placeholder="YYYY-MM-DD"
                  openCalendarAriaLabel={selectedDate =>
                    "Choose start date" +
                    (selectedDate ? `, selected date is ${selectedDate}` : "")
                  }
                  nextMonthAriaLabel="Next month"
                  previousMonthAriaLabel="Previous month"
                  todayAriaLabel="Today"
                />
              </FormField>

              <FormField
                label="End Date (optional)"
                description="Filter logs until this date (YYYY-MM-DD)"
              >
                <DatePicker
                  value={endDate}
                  onChange={({ detail }) => setEndDate(detail.value)}
                  placeholder="YYYY-MM-DD"
                  openCalendarAriaLabel={selectedDate =>
                    "Choose end date" +
                    (selectedDate ? `, selected date is ${selectedDate}` : "")
                  }
                  nextMonthAriaLabel="Next month"
                  previousMonthAriaLabel="Previous month"
                  todayAriaLabel="Today"
                />
              </FormField>

              <Box variant="small" color="text-body-secondary">
                Note: The download link expires after 5 minutes. If you don't download immediately, you'll need to export again.
              </Box>
            </>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
});

export default ExportAuditButton;
